import { useState } from 'react';
import { RelationshipGlassCard } from '@/components/contact-intelligence/RelationshipGlassCard';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Sample data matching the exact structure provided
const sampleData = {
  "rel_id": "12345678-1234-5678-1234-567812345678",
  "entity": {
    "kind": "organization",
    "display_name": "McKinsey & Company",
    "role_title": "Strategic Partner",
    "timezone": "America/New_York"
  },
  "context": {
    "type": "client",
    "tier": "B",
    "stage": "active",
    "objective": "Deepen collaboration for Q1 transformation initiative",
    "owner_person_id": "aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee",
    "owner_name": "John Petty",
    "value_currency": "USD",
    "value_amount": 280000,
    "expected_cadence_days": 14
  },
  "scores": {
    "as_of_date": "2025-10-30",
    "window": "30d",
    "health": 76,
    "dimensions": [
      {
        "name": "trust",
        "value": 87,
        "delta": 4,
        "confidence": 0.91,
        "top_signals": [
          "On-time rate 94%",
          "No recent cancellations"
        ],
        "spark": [72, 75, 79, 84, 87]
      },
      {
        "name": "alignment",
        "value": 73,
        "delta": 0,
        "confidence": 0.82,
        "top_signals": [
          "Goal similarity 0.78",
          "Initiative overlap: Q1 roadmap"
        ],
        "spark": [70, 71, 72, 73, 73]
      },
      {
        "name": "energy",
        "value": 65,
        "delta": -6,
        "confidence": 0.76,
        "top_signals": [
          "Cadence gap: 17d vs 14d",
          "Fewer 1:1s"
        ],
        "spark": [74, 73, 71, 68, 65]
      },
      {
        "name": "access",
        "value": 92,
        "delta": 3,
        "confidence": 0.88,
        "top_signals": [
          "Median reply 4h",
          "EA verified"
        ],
        "spark": [88, 89, 90, 91, 92]
      },
      {
        "name": "reciprocity",
        "value": 60,
        "delta": -3,
        "confidence": 0.64,
        "top_signals": [
          "2 overdue we_owe",
          "1 delivered case study"
        ],
        "spark": [66, 65, 64, 62, 60]
      },
      {
        "name": "influence",
        "value": 81,
        "delta": 0,
        "confidence": 0.78,
        "top_signals": [
          "VP sponsor",
          "3 exec links"
        ],
        "spark": [80, 80, 81, 81, 81]
      },
      {
        "name": "affinity",
        "value": 75,
        "delta": 2,
        "confidence": 0.83,
        "top_signals": [
          "Positive tone +0.12",
          "Remembered preferences"
        ],
        "spark": [70, 72, 73, 74, 75]
      }
    ]
  },
  "benchmarks": {
    "cohort_name": "Tier-B clients",
    "delta_vs_p50": -7,
    "percentile": 38
  },
  "drift": {
    "value": 64,
    "trend": "down",
    "next_touch_due": "2025-11-02T10:30:00-04:00"
  },
  "signals": [
    {
      "name": "reply_time_p50",
      "value": 240,
      "unit": "minutes"
    },
    {
      "name": "mtg_accept_rate",
      "value": 0.78,
      "unit": "ratio"
    },
    {
      "name": "interactions_weighted_sum",
      "value": 11.2,
      "unit": "index"
    },
    {
      "name": "on_time_rate",
      "value": 0.94,
      "unit": "ratio"
    }
  ],
  "people_map": [
    {
      "person_id": "8a5c4d62-5d9c-4e6a-a223-7a3a4b8e6c11",
      "name": "Sarah Zhang",
      "role": "champion",
      "influence": 86,
      "last_touch": "2025-10-28T13:00:00-04:00",
      "next_touch": "2025-11-02T10:30:00-04:00"
    },
    {
      "person_id": "1dc3cbd8-2f5e-4d17-b5a8-49a7a8b0d1a2",
      "name": "Raj Patel",
      "role": "blocker",
      "influence": 72,
      "last_touch": "2025-10-20T11:00:00-04:00",
      "next_touch": null
    },
    {
      "person_id": "23e2d7ee-2c80-4f5b-af7f-92b6033c4b1c",
      "name": "Anna Lee (EA)",
      "role": "ea",
      "influence": 70,
      "last_touch": "2025-10-29T09:30:00-04:00",
      "next_touch": "2025-10-31T09:00:00-04:00"
    }
  ],
  "timeline": [
    {
      "id": "a1",
      "date": "2025-10-30T11:45:00-04:00",
      "kind": "voice",
      "title": "Voice Journal — follow-ups",
      "participants": ["John Petty"],
      "sentiment": "neutral",
      "preview": "Saving info... add meeting later this week, draft email, add today's event.",
      "actions": ["create note", "create meeting", "send email", "create meeting"]
    },
    {
      "id": "a2",
      "date": "2025-10-29T14:10:00-04:00",
      "kind": "email",
      "title": "Follow‑up: Pilot scope",
      "participants": ["John Petty", "Sarah Zhang"],
      "sentiment": "positive",
      "preview": "Sharing updated scope and next steps...",
      "actions": ["draft reply"]
    },
    {
      "id": "a3",
      "date": "2025-10-28T10:00:00-04:00",
      "kind": "meeting",
      "title": "Transformation sync",
      "participants": ["John Petty", "Sarah Zhang", "Dev team"],
      "sentiment": "neutral",
      "preview": "Roadmap alignment; action items captured",
      "attachments": ["notes://transformation-sync-2025-10-28"]
    },
    {
      "id": "a4",
      "date": "2025-10-26T09:20:00-04:00",
      "kind": "email",
      "title": "Intro from Bob",
      "participants": ["Bob Dredger", "Sarah Zhang", "John Petty"],
      "sentiment": "positive",
      "preview": "Connecting you both to discuss Q1 initiative"
    }
  ],
  "voice_journal": {
    "transcript": "Testy test test test. Saving stuff to McKinsey and Company. --- Add a meeting for later this week, add an email that I need to draft up and send about the recent meeting, and add an event for today in-person meeting.",
    "sentiment": "neutral",
    "insights": [
      "Engagement with McKinsey is active.",
      "Schedule follow-up meeting this week.",
      "Draft email summarizing last meeting.",
      "Add today's in-person event."
    ],
    "detected_actions": [
      {
        "title": "Create note: McKinsey follow-ups",
        "predicted_lift": 4,
        "confidence": 0.62,
        "action_type": "note"
      },
      {
        "title": "Schedule 30‑min sync this week",
        "predicted_lift": 8,
        "confidence": 0.7,
        "action_type": "schedule",
        "payload": { "duration": 30 }
      },
      {
        "title": "Draft follow‑up email re: last meeting",
        "predicted_lift": 6,
        "confidence": 0.58,
        "action_type": "email"
      },
      {
        "title": "Add today's in‑person meeting",
        "predicted_lift": 3,
        "confidence": 0.55,
        "action_type": "schedule",
        "payload": { "duration": 60 }
      }
    ],
    "tags": ["McKinsey", "meetings", "emails", "task management"],
    "associated_contact": {
      "display": "McKinsey & Company",
      "email": "publishing@email.mckinsey.com"
    }
  },
  "actions": [
    {
      "title": "Exec‑to‑exec 15‑min ask",
      "predicted_lift": 10,
      "confidence": 0.62,
      "action_type": "email"
    },
    {
      "title": "Warm intro via Sarah → Ops",
      "predicted_lift": 12,
      "confidence": 0.7,
      "action_type": "intro"
    },
    {
      "title": "Send case study: Transformation Q1",
      "predicted_lift": 8,
      "confidence": 0.58,
      "action_type": "email"
    }
  ],
  "provenance": {
    "last_sync": "2025-10-30T14:05:00Z",
    "sample_size": 28,
    "confidence": 0.84,
    "dq_score": 88
  },
  "tags": ["McKinsey", "client", "transformation", "Q1"]
};

export default function RelationshipGlassCardDemo() {
  const handleAction = (actionType: string, payload?: any) => {
    console.log('Action triggered:', actionType, payload);
    
    let message = `Action: ${actionType}`;
    if (payload) {
      message += ` (${JSON.stringify(payload)})`;
    }
    
    toast.success(message, {
      description: 'This would trigger your backend action handler',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Relationship Intelligence Hub
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            High-end glass UI with progressive disclosure • Hover to reveal deeper insights
          </p>
        </div>

        <div className="flex justify-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              toast.info('Tip: Hover over metric tiles to see confidence scores and signals');
            }}
          >
            💡 Interaction Tips
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              toast.info('Tip: Hover the right edge of the screen to see data quality metrics');
            }}
          >
            📊 Data Spine
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              toast.info('Tip: Click timeline items to expand details and see attachments');
            }}
          >
            📅 Timeline
          </Button>
        </div>

        <RelationshipGlassCard 
          data={sampleData} 
          onAction={handleAction}
        />

        <div className="mt-8 p-6 backdrop-blur-xl bg-white/60 dark:bg-gray-950/40 border border-white/20 dark:border-white/10 rounded-xl">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Integration Guide
          </h3>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <p>
              <strong>1. Replace sample data:</strong> Use your API response with the same structure
            </p>
            <p>
              <strong>2. Wire action handlers:</strong> Implement <code className="px-2 py-1 bg-gray-200 dark:bg-gray-800 rounded">onAction</code> callback
            </p>
            <p>
              <strong>3. Customize styling:</strong> Edit <code className="px-2 py-1 bg-gray-200 dark:bg-gray-800 rounded">glassStyles</code> tokens in the component
            </p>
            <p>
              <strong>4. Add to routing:</strong> Import and use in your dashboard or contact detail views
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
